import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isEmailVerified, useMaybeAuth } from '../auth/AuthContext';
import type { Subscription } from '../auth/AuthContext';
import AlertBanner from './AlertBanner';
import { fetchSubscription, openBillingPortal } from '../services/billingService';
import { TIER, tierLabel } from '../constants/tiers';
import { formatCompactDate } from '../util/formatDate';
import { useMinimumVisible } from '../util/loading';
import { ROUTES } from '../util/routes';
import type { AccountNotice } from '../util/authEvents';
import './AccountPanel.scss';

type LiveStatus = 'loading' | 'ready' | 'error';

/**
 * One sentence that tells the customer exactly where their subscription stands.
 */
function describeSubscription(subscription: Subscription): { text: string; warn: boolean } {
  const renews = formatCompactDate(subscription.renews_at);
  const trialEnds = formatCompactDate(subscription.trial_ends_at);
  const ends = formatCompactDate(subscription.ends_at);

  switch (subscription.state) {
    case 'trialing':
      return { text: trialEnds ? `Free trial — ends on ${trialEnds}` : 'Free trial', warn: false };
    case 'canceling':
      return {
        text: ends ? `Cancels on ${ends} — you keep Pro until then` : 'Cancels at the end of this period',
        warn: true
      };
    case 'past_due':
      return { text: 'Payment issue — update your payment method to keep Pro', warn: true };
    case 'incomplete':
      return { text: 'Payment incomplete — finish payment to activate Pro', warn: true };
    default:
      return { text: renews ? `Active — renews on ${renews}` : 'Active', warn: false };
  }
}

/**
 * Remove `billing=return` (set by the billing portal's return URL) so a reload doesn't force
 * another Stripe check.
 */
function consumePortalReturnFlag(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('billing') !== 'return') return false;
  params.delete('billing');
  const qs = params.toString();
  try {
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  } catch (_e) {
    // ignore
  }
  return true;
}

/** The nearest ancestor that scrolls its own content, or null when only the page itself scrolls. */
function scrollContainerOf(el: HTMLElement | null): HTMLElement | null {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

/**
 * The account view itself — profile, subscription, work and sign-in — with no container of its own.
 *
 * Rendered in two places, and the contents are identical in both: the `/settings` PAGE (which must
 * keep existing because `config/billing.php` sends Stripe's billing portal back to
 * `/settings?billing=return`, a hop we do not control) and the My Account MODAL opened from the
 * launcher. Only the surrounding chrome differs, which is why it lives here rather than in a view.
 *
 * `headingId` lets the modal label its dialog with this heading; the page leaves it undefined.
 *
 * `onSignedOut` is what happens AFTER a successful log out, and the container decides it, because
 * only the container knows what is behind the panel. The page sends you home; the modal just
 * closes, leaving your editor work where it was. Logging out is an account action, not a document
 * action — it must never be the thing that destroys an unchopped image.
 *
 * `frozen` keeps the panel showing the account it was already showing even after the user signs
 * out. The modal passes `!isOpen` so the dialog fades out on the content you were reading; without
 * it the cards collapsed to "Sign in to see your account" and the dialog visibly shrank mid-fade.
 *
 * It has to live HERE rather than in ModalShell. A shell that froze the last child ELEMENT was
 * tried and does not work: this component reads auth from context, so it re-renders on sign-out
 * whichever element its parent hands down — and a frozen element would also pin `frozen` itself
 * to the value it had while open. Each dialog freezes what it actually branches on; AuthModal
 * does the same with its own `isOpen` guard.
 */
export default function AccountPanel({
  headingId,
  onSignedOut,
  frozen = false,
  notice = null
}: {
  headingId?: string;
  onSignedOut?: () => void;
  frozen?: boolean;
  notice?: AccountNotice | null;
}) {
  const auth = useMaybeAuth();
  const liveUser = auth?.user || null;
  // Idempotent render-phase write: re-running produces the same value, so StrictMode is fine.
  const lastUserRef = useRef(liveUser);
  if (liveUser) lastUserRef.current = liveUser;
  const user = liveUser ?? (frozen ? lastUserRef.current : null);
  const isAuthLoading = auth?.isLoading ?? false;

  const [liveStatus, setLiveStatus] = useState<LiveStatus>('loading');
  const [liveSubscription, setLiveSubscription] = useState<Subscription | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [verifyState, setVerifyState] = useState<'idle' | 'sending' | 'sent' | 'verified'>('idle');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  // Same 300–700ms floor the upload and resume transitions use: the Stripe hop and the
  // verification POST can both resolve faster than a state change can be perceived.
  const isSending = verifyState === 'sending';
  const redirectingHeld = useMinimumVisible(isRedirecting);
  const sendingHeld = useMinimumVisible(isSending);
  const showRedirecting = isRedirecting || redirectingHeld;
  const showSending = isSending || sendingHeld;
  // Errors wait for the hold rather than cancelling it — see Pricing.tsx. Cancelling removes the
  // contradictory frame but restores the flicker, because a failure usually returns fastest.
  const visiblePortalError = showRedirecting ? null : portalError;
  const visibleAccountError = showSending ? null : accountError;

  /*
   * The banner sits under the title only while the top of the panel is on screen. In the My Account
   * dialog the BODY scrolls and the dialog's × floats over its top-right corner, so with the body
   * scrolled 23–87px the banner slid up under that × and the two dismiss buttons came to rest 3px
   * apart again; scrolled further, the failure was simply out of sight. That is how a click retry
   * that scrolled the body failed the v3.0.0-rc.2 release gate on CI WebKit. So when a failure
   * appears, whatever scrolls the panel goes back to its top. A LAYOUT effect, so the banner is
   * never painted, or measured, in the scrolled position. On the /settings page only the window
   * scrolls, and this does nothing.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!visiblePortalError) return;
    const scroller = scrollContainerOf(panelRef.current);
    if (scroller) scroller.scrollTop = 0;
  }, [visiblePortalError]);

  // Every visit asks the server, which checks Stripe (throttled server-side), so what is shown is
  // current without the customer ever refreshing. Returning from the portal forces one check.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const returningFromPortal = consumePortalReturnFlag();

    (async() => {
      try {
        const subscription = await fetchSubscription(returningFromPortal);
        if (cancelled) return;
        setLiveSubscription(subscription);
        setLiveStatus('ready');
        // A portal change can alter the tier, so refresh the app-wide user as well.
        if (returningFromPortal) await auth?.refreshUser();
      } catch (_e) {
        if (!cancelled) setLiveStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManage = async() => {
    setPortalError(null);
    setIsRedirecting(true);
    try {
      window.location.assign(await openBillingPortal());
    } catch (e) {
      setIsRedirecting(false);
      // The RAW cause, not a sentence: AlertBanner runs it through `hintForError`, which is what
      // keeps unreviewed server text off the screen and the mapping in one file. The panel used to
      // write its own "Please try again" here — useless advice for either real cause, since
      // neither a missing config nor a missing customer record is transient.
      setPortalError(e instanceof Error ? e.message : 'Could not open billing portal');
    }
  };

  const handleResendVerification = async() => {
    setAccountError(null);
    setVerifyState('sending');
    try {
      const outcome = await auth?.requestEmailVerification();
      // The server tells us what it DID. On a deployment with no mailer it verifies directly, and
      // saying "check your inbox" there is how someone ends up waiting for an email that is not
      // coming. Refresh so the "Not verified" badge goes with the message.
      if (outcome === 'verified_without_email' || outcome === 'already_verified') {
        setVerifyState('verified');
        await auth?.refreshUser();
      } else {
        setVerifyState('sent');
      }
    } catch (_e) {
      setVerifyState('idle');
      setAccountError('Could not send the verification email. Please try again.');
    }
  };

  const handleLogout = async() => {
    setAccountError(null);
    try {
      await auth?.logout();
      // No navigation here on purpose. `setUser(null)` already re-evaluates every capability gate
      // (layers, snap, drafts, history) on the next render, so the signed-out state is correct
      // without reloading — and a reload would throw away an unchopped image.
      onSignedOut?.();
    } catch (_e) {
      setAccountError('Could not log out. Please try again.');
    }
  };

  const handleDeleteAccount = async() => {
    setAccountError(null);
    setIsDeleting(true);
    try {
      await auth?.deleteAccount();
      window.location.assign('/');
    } catch (_e) {
      setIsDeleting(false);
      setAccountError('Could not delete your account. Please try again.');
    }
  };

  // Until the live check answers, show the webhook-synced state already loaded with the user.
  const subscription = liveStatus === 'ready' ? liveSubscription : user?.subscription ?? null;
  // A record is enough on its own: the live check can answer before `user.tier` catches up after a
  // fresh checkout. Short of that, the effective tier decides, which is what keeps this card and
  // the Plan row above it from ever disagreeing.
  const isPro = Boolean(subscription) || user?.tier === TIER.PRO;

  return (
    <div className="settings" ref={ panelRef }>
      <h2 id={ headingId }>Account</h2>

      { /*
         * Failures belong up here, not under the control that triggered them — the same move made
         * on the pricing page, where the checkout error sat inside `.plan-cta` and shoved the
         * feature list down 32px when it appeared. Here it sat under "Manage subscription" and
         * pushed the Stripe footnote around, dressing a whole-environment problem ("this deployment
         * has no Stripe keys") as a property of one button.
         *
         * BELOW the heading, not above it. Above, it ran into the dialog's floating close button —
         * its own dismiss × landed 3px from that one, two ways to dismiss something close enough to
         * hit the wrong one. Under the title it clears that corner on its own, with no reserved
         * space anywhere and the full width for its text.
         *
         * It lives in the panel rather than either container so it reaches BOTH: the /settings page
         * and the My Account dialog share this component and nothing else.
         */ }
      <AlertBanner cause={ visiblePortalError } onDismiss={ () => setPortalError(null) } />

      {isAuthLoading && !user && <p className="settings-note">Loading…</p>}

      {!isAuthLoading && !user && (
        <p>Sign in to see your account and subscription.</p>
      )}

      {user && (
        <>
          <section className="settings-card" aria-labelledby="settings-profile">
            <h3 id="settings-profile" className="settings-card-title">Profile</h3>
            <div className="settings-row">
              <span className="settings-label">Name</span>
              <span className="settings-value">{ user.name }</span>
            </div>
            <div className="settings-row">
              <span className="settings-label">Email</span>
              <span className="settings-value">
                { user.email }{' '}
                {isEmailVerified(user) ? (
                  <span className="settings-badge settings-badge-ok">Verified</span>
                ) : (
                  <span className="settings-badge settings-badge-warn">Not verified</span>
                )}
              </span>
            </div>
            {!isEmailVerified(user) && (
              <div className="settings-inline">
                <button
                  type="button"
                  className="settings-link-btn"
                  onClick={ handleResendVerification }
                  disabled={ verifyState !== 'idle' || showSending }
                >
                  { /* There was no visible in-flight state at all: the label stayed "Resend…"
                       while disabled, then jumped to the confirmation. "Sending…" held for the
                       minimum makes the click feel acknowledged. */ }
                  { showSending
                    ? 'Sending…'
                    : verifyState === 'verified' ? 'No mailer here — verified directly'
                    : verifyState === 'sent' ? 'Verification email sent — check your inbox'
                    : 'Resend verification email' }
                </button>
              </div>
            )}
            <div className="settings-row">
              <span className="settings-label">Plan</span>
              <span className="settings-value">{ tierLabel(user.tier) }</span>
            </div>
          </section>

          <section className="settings-card" aria-labelledby="settings-subscription">
            <h3 id="settings-subscription" className="settings-card-title">Subscription</h3>

            {isPro && notice === 'already-pro' && (
              // They pressed "Get Pro" signed out, then signed in to an account that has it. The
              // dialog opened for them, so it says why rather than leaving them to work it out.
              <p className="settings-status" role="status">You already have Pro, so there’s nothing to buy.</p>
            )}

            {isPro ? (
              /*
               * ONE SHAPE FOR PRO, whether or not a billing record sits behind it.
               *
               * The card follows the EFFECTIVE tier, exactly like the Plan row above it, so the two
               * can never disagree — it used to say "You're on the Free plan. Upgrade to Pro" while
               * that row said "Plan: Pro", offering to sell Pro to someone already being treated as
               * Pro. Fixing the words was only half of it: a Pro with no record then got a stub
               * card with no button and no footnote, so the layout itself still diverged, and an
               * admin testing locally was reviewing a card production never renders.
               *
               * Only what is genuinely unknown is withheld. Without a record there is no billing
               * interval, so the plan line is "Pro" rather than "Pro · Monthly", and the status is
               * the same bare "Active" a real subscription falls back to when Stripe gives no
               * renewal date. The button stays, and says why it cannot open when pressed.
               */
              <>
                <div className="settings-plan">
                  { subscription
                    ? `Pro · ${subscription.interval === 'yearly' ? 'Yearly' : 'Monthly'}`
                    : 'Pro' }
                </div>
                {(() => {
                  const { text, warn } = subscription
                    ? describeSubscription(subscription)
                    // No record at all, so there is no date to miss and nothing scheduled to end —
                    // which is worth SAYING rather than leaving a bare "Active" that reads like a
                    // line with its second half missing. Distinct from a real subscription whose
                    // renewal date Stripe did not return: that one has an end, we just do not know
                    // it, and claiming otherwise would invent a fact about someone's billing.
                    : { text: 'Active — doesn’t expire', warn: false };
                  return <p className={ `settings-status${warn ? ' settings-status-warn' : ''}` }>{ text }</p>;
                })()}
                <button
                  type="button"
                  className="settings-btn"
                  onClick={ handleManage }
                  disabled={ showRedirecting }
                >
                  { showRedirecting ? 'Redirecting to Stripe…' : 'Manage subscription' }
                </button>
                <p className="settings-note">
                  Payment details, invoices, plan changes and cancellation are handled securely by Stripe.
                </p>
              </>
            ) : (
              <>
                <p className="settings-status">You’re on the Free plan.</p>
                <a className="settings-btn" href={ ROUTES.PRICING }>Upgrade to Pro</a>
              </>
            )}

            {liveStatus === 'error' && (
              <p className="settings-note">
                We couldn’t reach Stripe just now — showing your last known status.
              </p>
            )}
          </section>

          <section className="settings-card" aria-labelledby="settings-work">
            <h3 id="settings-work" className="settings-card-title">Your work</h3>
            <a href="/my-slices">My Slices</a>
          </section>

          <section className="settings-card" aria-labelledby="settings-session">
            <h3 id="settings-session" className="settings-card-title">Sign-in</h3>
            <button type="button" className="settings-btn settings-btn-secondary" onClick={ handleLogout }>
              Log out
            </button>
            <div className="settings-danger">
              {confirmDelete ? (
                <>
                  <p className="settings-status settings-status-warn">
                    This permanently deletes your account{ subscription ? ' and cancels your subscription' : '' }.
                    This can’t be undone.
                  </p>
                  <button
                    type="button"
                    className="settings-btn settings-btn-danger"
                    onClick={ handleDeleteAccount }
                    disabled={ isDeleting }
                  >
                    { isDeleting ? 'Deleting…' : 'Yes, delete my account' }
                  </button>{' '}
                  <button
                    type="button"
                    className="settings-link-btn"
                    onClick={ () => setConfirmDelete(false) }
                    disabled={ isDeleting }
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="settings-link-btn settings-link-danger"
                  onClick={ () => setConfirmDelete(true) }
                >
                  Delete account
                </button>
              )}
            </div>
            {visibleAccountError && <p className="settings-error" role="alert">{ visibleAccountError }</p>}
          </section>
        </>
      )}
    </div>
  );
}
